import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import WallpaperRounded from "@mui/icons-material/WallpaperRounded";
import AIDescriptionField from "../../AIDescriptionField";
import ImagePicker from "../../ImagePicker";
import GalleryImage from "../../GalleryImage";
import BottomButton from "../../BottomButton";
import { FullScreenLoader } from "../../FullScreenLoader";
import Footer from "../Footer";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import Brand from "../../../api/Brand";
import {
    getCloudinaryOriginalUrl,
    uploadToCloudinary,
} from "../../../utils/cloudinaryUtils";
import { getPreferredLanguage } from "../../../utils/languageUtils";
import ImageCropperModal from "../../ImageCropperModal";
import AlertModal from "../../Modals/AlertModal";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FORM_LABEL_SX } from "../../../constants/styles";

const WallpaperEdit = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [brand, setBrand] = useState<Brand | undefined>();
    const [description, setDescription] = useState("");
    const [descriptionError, setDescriptionError] = useState<string | null>(
        null
    );
    const [imagePreview, setImagePreview] = useState<string | undefined>();
    const [image, setImage] = useState<string | null | undefined>(undefined);
    const [, setIsWallpaperConfigured] = useState(true);
    const [previewMode, setPreviewMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);

    // crop modal state
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);

    // derive original for recropping (strip transforms)
    const originalForCrop = useMemo(() => {
        return imagePreview ? getCloudinaryOriginalUrl(imagePreview) : null;
    }, [imagePreview]);

    const isSaveDisabled =
        image === undefined && description === (brand?.wallpaperText ?? "");

    useEffect(() => {
        if (!uuid || !brand_id) return;

        Brand.info(uuid, brand_id)
            .then((brand) => {
                if (!brand) return;
                setBrand(brand);
                setDescription(brand.wallpaperText ?? "");
                setImagePreview(
                    brand.wallpaper ||
                        sessionStorage.getItem(`${brand.key}_wallpaper`) ||
                        undefined
                );
                setIsWallpaperConfigured(!!brand.setupSteps.wallpaper);
            })
            .catch(console.error);
    }, [uuid, brand_id]);

    const onWallpaperImageUpload = async (base64Image?: string) => {
        if (!base64Image) return;

        try {
            const stagingUrl = await uploadToCloudinary(base64Image);
            if (stagingUrl) {
                setCropSourceUrl(stagingUrl);
                setCropOpen(true);
            }
        } catch (error) {
            console.error("Error during wallpaper upload:", error);
        }
    };

    const onWallpaperImageRemove = () => {
        setImage(null);
        setImagePreview(undefined);
        sessionStorage.removeItem(`${brand!.key}_wallpaper`);
    };

    // When user confirms crop, we receive a Cloudinary URL with transforms
    const handleCropSave = (finalTransformedUrl: string) => {
        setImage(finalTransformedUrl); // this is what goes to BE on Save
        setImagePreview(finalTransformedUrl); // this is what we render immediately in UI
        sessionStorage.setItem(`${brand!.key}_wallpaper`, finalTransformedUrl);
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    // When user cancels crop (keeps current avatar untouched)
    const handleCropCancel = () => {
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    const onSave = useCallback(async () => {
        if (!uuid || !brand_id || !brand || saving) return;

        // Nothing changed, no need to send anything, just go back
        if (image === undefined && description === (brand.wallpaperText ?? ""))
            return navigate("/");

        setSaving(true);
        const ok = await Brand.updateWallpaper(uuid, brand_id, {
            wallpaper: image,
            wallpaperText: description,
            originalLang: getPreferredLanguage(),
        });
        setSaving(false);

        // updateWallpaper returns false on any failed/non-200 request. Don't
        // navigate away pretending it saved — surface it so the user can retry.
        if (ok) navigate("/");
        else setSaveError(true);
    }, [uuid, brand_id, brand, description, image, navigate, saving]);

    const primary = theme.palette.primary.main;
    const easing = "cubic-bezier(0.4, 0, 0.2, 1)";

    // Swipe-up on collapsed bottom sheet to exit preview
    const swipeStartY = useRef(0);
    const onSheetTouchStart = useCallback((e: React.TouchEvent) => {
        if (!previewMode) return;
        swipeStartY.current = e.touches[0].clientY;
    }, [previewMode]);
    const onSheetTouchEnd = useCallback((e: React.TouchEvent) => {
        if (!previewMode) return;
        const dy = e.changedTouches[0].clientY - swipeStartY.current;
        if (dy < -50) setPreviewMode(false);
    }, [previewMode]);

    if (!brand) return <FullScreenLoader />;

    return (
        <>
            <Stack
                direction="column"
                width="100%"
                flex={1}
                sx={{
                    overflowY: previewMode ? "hidden" : "auto",
                    overflowX: "hidden",
                    bgcolor: "white",
                }}
            >
                {/* ── Hero: wallpaper preview area ── */}
                <ButtonBase
                    component="div"
                    onClick={() => {
                        if (previewMode) return;
                        if (imagePreview) {
                            setCropSourceUrl(
                                originalForCrop ?? imagePreview
                            );
                            setCropOpen(true);
                        }
                    }}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        textAlign: "left",
                        cursor:
                            !previewMode && imagePreview
                                ? "pointer"
                                : "default",
                        flex: previewMode ? 1 : "0 0 auto",
                        transition: `flex 0.45s ${easing}`,
                    }}
                    disableRipple={previewMode || !imagePreview}
                >
                    <Stack
                        width="100%"
                        flex={previewMode ? 1 : undefined}
                        justifyContent="flex-start"
                        sx={{
                            minHeight: previewMode ? 0 : 280,
                            position: "relative",
                            boxSizing: "border-box",
                            px: 2.5,
                            pb: previewMode ? 0 : "48px",
                            pt: "48px",
                            transition: `min-height 0.45s ${easing}, padding 0.45s ${easing}`,
                            // Wallpaper image or brand gradient fallback
                            ...(imagePreview
                                ? {
                                      backgroundImage: `url(${imagePreview})`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                  }
                                : {
                                      background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                                  }),
                            // Noise texture (matches HeroBackdrop)
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                opacity: imagePreview ? 0 : 0.04,
                                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "repeat",
                                backgroundSize: "128px 128px",
                                pointerEvents: "none",
                            },
                        }}
                    >
                        {/* Back arrow */}
                        <ButtonBase
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(-1 as any);
                            }}
                            sx={{
                                position: "absolute",
                                top: 12,
                                left: 10,
                                zIndex: 2,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ColoredIcon
                                icon={BackIcon}
                                color="white"
                                size="28px"
                            />
                        </ButtonBase>

                        {/* PREVIEW pill — clickable to enter preview mode */}
                        <ButtonBase
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewMode(true);
                            }}
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                bgcolor: previewMode
                                    ? "rgba(60,60,60,0.4)"
                                    : "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                borderRadius: "12px",
                                px: 1.25,
                                py: 0.25,
                                zIndex: 2,
                                transition: `background-color 0.3s ease`,
                            }}
                            disableRipple={previewMode}
                        >
                            <Typography
                                fontSize="10px"
                                fontWeight={700}
                                color="white"
                                textTransform="uppercase"
                                letterSpacing="1px"
                            >
                                Preview
                            </Typography>
                        </ButtonBase>

                        {/* Empty state hint — normal mode only */}
                        {!imagePreview && !previewMode && (
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1,
                                    pt: 2,
                                }}
                            >
                                <WallpaperRounded
                                    sx={{
                                        fontSize: 48,
                                        color: "rgba(255,255,255,0.35)",
                                    }}
                                />
                                <Typography
                                    fontSize="13px"
                                    color="rgba(255,255,255,0.5)"
                                    mt={1}
                                >
                                    Upload a wallpaper to preview
                                </Typography>
                            </Stack>
                        )}

                        {/* ── Preview mode: full-screen client app layout ── */}
                        {previewMode && (
                            <Stack
                                flex={1}
                                justifyContent="flex-end"
                                alignItems="center"
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    pb: "100px",
                                    "@keyframes previewCardIn": {
                                        from: {
                                            opacity: 0,
                                            transform: "translateY(20px)",
                                        },
                                        to: {
                                            opacity: 1,
                                            transform: "translateY(0)",
                                        },
                                    },
                                    animation: `previewCardIn 0.4s ease-out 0.2s both`,
                                }}
                            >
                                {/* Frosted white card — matches client Home.tsx CLIENT_GLASS_SX */}
                                <Stack
                                    width="85%"
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    padding={2}
                                    spacing={2}
                                    borderRadius="20px"
                                    sx={{
                                        bgcolor: "rgba(255,255,255,0.65)",
                                        backdropFilter: "blur(20px)",
                                        WebkitBackdropFilter: "blur(20px)",
                                        border: "1px solid rgba(255,255,255,0.5)",
                                    }}
                                >
                                    {description.trim() && (
                                        <Stack width="90%">
                                            <Typography
                                                fontWeight={400}
                                                fontSize="14px"
                                                lineHeight="18.2px"
                                                width="100%"
                                                textAlign="center"
                                                sx={{
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {description}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {/* Faux "Online Booking" button — matches client app Button variant="large" */}
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 56,
                                            borderRadius: "14px",
                                            bgcolor: primary,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography
                                            color="white"
                                            fontWeight={600}
                                            fontSize="16px"
                                            lineHeight="22.4px"
                                        >
                                            Online Booking
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </ButtonBase>

                {/* ── Bottom sheet ── */}
                <Stack
                    onClick={
                        previewMode
                            ? () => setPreviewMode(false)
                            : undefined
                    }
                    onTouchStart={onSheetTouchStart}
                    onTouchEnd={onSheetTouchEnd}
                    sx={{
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        marginTop: "-24px",
                        position: "relative",
                        zIndex: 1,
                        px: 2,
                        pt: previewMode ? 1.5 : 3,
                        pb: previewMode ? 1.5 : "180px",
                        boxSizing: "border-box",
                        cursor: previewMode ? "pointer" : "default",
                        overflow: "hidden",
                        maxHeight: previewMode ? 56 : 1500,
                        transition: `padding 0.45s ${easing}, max-height 0.45s ${easing}`,
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                >
                    {/* Pull handle + "Tap to edit" — visible in preview mode */}
                    <Stack
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                            maxHeight: previewMode ? 40 : 0,
                            opacity: previewMode ? 1 : 0,
                            overflow: "hidden",
                            transition: `all 0.35s ${easing}`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: "rgba(0,0,0,0.15)",
                            }}
                        />
                        <Typography
                            fontSize="12px"
                            fontWeight={500}
                            color="text.secondary"
                        >
                            Swipe up to edit
                        </Typography>
                    </Stack>

                    {/* Content — hidden in preview mode */}
                    <Stack
                        spacing={3}
                        sx={{
                            display: previewMode ? "none" : "flex",
                            opacity: previewMode ? 0 : 1,
                            transition: `opacity 0.3s ${easing}`,
                        }}
                    >
                        {/* WALLPAPER IMAGE section */}
                        <Stack spacing={1.5} width="100%">
                            <Typography
                                fontSize="11px"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                            >
                                Wallpaper Image
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                            >
                                {imagePreview && (
                                    <ButtonBase
                                        onClick={() => {
                                            setCropSourceUrl(
                                                originalForCrop ?? imagePreview
                                            );
                                            setCropOpen(true);
                                        }}
                                        sx={{
                                            borderRadius: "12px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <GalleryImage
                                            width="64px"
                                            height="64px"
                                            borderRadius="12px"
                                            image={imagePreview}
                                            expandable={false}
                                        />
                                    </ButtonBase>
                                )}
                                <ImagePicker
                                    withAI={false}
                                    text="Upload image"
                                    onUpload={onWallpaperImageUpload}
                                />
                            </Stack>
                            <Typography sx={FORM_LABEL_SX}>
                                Portrait photos work best. Keep key details centered — edges may be cropped on smaller screens.
                            </Typography>
                        </Stack>

                        {/* WELCOME TEXT section */}
                        <Stack spacing={1.5} width="100%">
                            <Typography
                                fontSize="11px"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                            >
                                Welcome Text
                            </Typography>
                            <Typography sx={FORM_LABEL_SX}>
                                Text shown on your app's home screen
                            </Typography>
                            <AIDescriptionField
                                placeholder="Tell us about yourself"
                                value={description}
                                setText={(val) => {
                                    setDescriptionError(null);
                                    setDescription(val);
                                }}
                                contextType="wallpaper_cta"
                                uuid={uuid}
                                brandKey={brand_id}
                                width="100%"
                            />
                            {descriptionError && (
                                <Typography color="error.main">
                                    {descriptionError}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            {/* Wallpaper crop modal */}
            <ImageCropperModal
                open={cropOpen}
                src={cropSourceUrl}
                onCancel={handleCropCancel}
                onSave={handleCropSave}
                onDelete={onWallpaperImageRemove}
                mode="wallpaper"
                outSize={{ w: 1080, h: 2340 }}
                wallpaperSafeArea={{ top: 200, bottom: 200 }}
            />
            {!previewMode && (
                <BottomButton
                    sticky
                    footer
                    onClick={onSave}
                    disabled={isSaveDisabled || saving}
                >
                    {saving ? "Saving…" : "Save Changes"}
                </BottomButton>
            )}
            {!previewMode && <Footer tab="admin" />}

            <AlertModal
                open={saveError}
                onClose={() => setSaveError(false)}
                headingText="Couldn't save"
                alertText="Your wallpaper changes didn't save. Please check your connection and try again."
                buttonText="Try again"
            />
        </>
    );
};

export default WallpaperEdit;
